<template>
  <div class="h-full">
    <div class="mx-1">
      <SymbolSearch
        wrapper-class="w-[20%]"
        show-postfix
        :is-show-market-depth="isShowMarketDepth"
        is-show-recent-search
        :active-symbol-name="activeSymbolName"
        @update-symbol="(symbol) => updateActiveIsin(symbol.isin)"
        @handle-market-depth="isShowMarketDepth = !isShowMarketDepth"
      />
    </div>

    <div class="flex h-[calc(100%-46px)] mx-1">
      <div
        class="flex-col bg-neutral-secondary rounded-md h-full relative w-full"
        :class="[isShowMarketDepth ? 'min-w-[calc(100%-30rem)]' : 'w-full']"
      >
        <NTabs
          :model-value="selectedTab"
          container-class="flex-col-reverse "
          :content-class="[!vertical ? 'overflow-hidden' : 'overflow-auto']"
          class="bg-neutral-secondary pt-1"
          align="center"
          @update:model-value="handleTabChange"
        >
          <NTab
            v-gid="{
              module: 'symbol',
              screen: 'home',
              section: 'symbolDetail',
              component: 'chartTabButton',
            }"
          >
            <template #prepend>
              <NegarCandelChartIcon size="20" />
            </template>

            <template #title>
              <NText variant="caption1" weight="bold">
                {{ t('symbol.technical') }}
              </NText>
            </template>

            <ComponentState :fetch-status="symbolStatus">
              <template #loading>
                <img
                  src="@/assets/images/sample-technical-chart.svg"
                  alt="Technical chart"
                  class="my-auto"
                />
              </template>

              <template #error>
                <Error class="p-2" @retry="() => emit('getSymbol')" />
              </template>

              <SymbolTechnicalChart :symbol="symbol" />
            </ComponentState>
          </NTab>

          <NTab
            v-gid="{
              module: 'symbol',
              screen: 'home',
              section: 'symbolDetail',
              component: 'detailTabButton',
            }"
          >
            <template #prepend>
              <NegarPresentationLightIcon size="20" />
            </template>

            <template #title>
              <NText variant="caption1" weight="bold">
                {{ t('symbol.symbol_info') }}
              </NText>
            </template>

            <ComponentState :fetch-status="symbolStatus">
              <template #loading>
                <SymbolSkeleton />
              </template>

              <template #error>
                <Error class="p-2" @retry="() => emit('getSymbol')" />
              </template>

              <SymbolDetail
                :symbol="symbol"
                :portfolio-symbols="portfolioSymbols"
                :vertical="vertical"
              />
            </ComponentState>
          </NTab>

          <NTab
            v-gid="{
              module: 'symbol',
              screen: 'home',
              section: 'symbolDetail',
              component: 'industryTabButton',
            }"
          >
            <template #prepend>
              <NegarIndustryIcon size="20" />
            </template>

            <template #title>
              <NText variant="caption1" weight="bold">
                {{ t('symbol.industryGroups') }}
              </NText>
            </template>

            <IndustryGroupsTable :isin="isin" />
          </NTab>
        </NTabs>
      </div>

      <div
        v-if="isShowMarketDepth"
        class="bg-neutral-secondary rounded-md basis-[30rem] h-full ms-1"
      >
        <MarketDepth :symbol="symbol" />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  NegarPresentationLightIcon,
  NegarCandelChartIcon,
  NegarIndustryIcon,
} from '@negar/icons';
import { isEmpty } from '@libs/utils';
import { Instrument, PortfolioItem } from '@libs/api-reference';
import { QueryStatus } from '@libs/use';

import {
  SymbolDetail,
  SymbolTechnicalChart,
  SymbolSearch,
  MarketDepth,
  IndustryGroupsTable,
  useActiveSymbolIsin,
  SymbolSkeleton,
} from '@/modules/symbol';

import eventBus from '@/composables/event/eventBus';

import Error from '@/components/Error.vue';
import ComponentState from '@/components/ComponentState.vue';

type SymbolEmitsType = {
  (e: 'getSymbol'): void;
  (e: 'handleShowTechnicalChart', value: boolean, isExpanded: boolean): void;
};
type SymbolPropsType = {
  vertical?: boolean;
  portfolioSymbols: PortfolioItem[];
  showTechnicalChart?: boolean;
  symbol: Instrument;
  symbolStatus: QueryStatus;
  isin: string;
};

const props = withDefaults(defineProps<SymbolPropsType>(), { vertical: false });

const emit = defineEmits<SymbolEmitsType>();

const Tabs = {
  chart: 0,
  detail: 1,
  industryGroups: 2,
} as const;

const isShowMarketDepth = ref(false);
const selectedTab = ref<number>(Tabs.detail);

const { t } = useI18n();

const { updateActiveIsin } = useActiveSymbolIsin();

eventBus.on('openIndustryGroups', () => {
  selectedTab.value = Tabs.industryGroups;
});

const handleTabChange = (newValue) => {
  selectedTab.value = newValue;

  emit(
    'handleShowTechnicalChart',
    newValue !== Tabs.detail,
    newValue === Tabs.chart
  );
};

const activeSymbolName = computed(() =>
  !isEmpty(props.symbol)
    ? `${props.symbol.instrumentName} - ${props.symbol.instrumentTitle} - ${props.symbol.exchangeName}`
    : ''
);
</script>
